// @ts-nocheck
// Adapted from tailuge/billiards (GPL-3.0) — src/utils/rack.ts
// Modified: pure ball positions only. Removed BallAppearance / Session / network.
import { Ball } from "./ball"
import { TableGeometry } from "./tablegeometry"
import { Vector3 } from "three"
import { roundVec, vec } from "./three-utils"
import { R } from "./constants"

export class Rack {
  // 改変(pool): ラックを密着させる。upstream の noise(=R*0.023+0.0015≈2.2mm) は
  // gap=2R+2*noise で球間に約4.3mmの隙間を生み、ブレイクで「うまく割れない」原因だった。
  // 実ラックは球が接触している前提なので、隙間をほぼ無くす（わずかな jitter は残し、
  // 完全対称ブレイクで手球が真後ろに跳ね返るのを避ける）。gap>2R は維持＝初期重なりなし。
  static readonly noise = Math.fround(R * 0.004)
  static readonly gap = 2 * R + 2 * Rack.noise
  static readonly up = new Vector3(0, 0, -1)
  static readonly spot = new Vector3(-TableGeometry.X / 2, 0, 0)
  static readonly across = new Vector3(0, Rack.gap, 0)
  static readonly down = new Vector3(Rack.gap, 0, 0)
  static readonly diagonal = Rack.across
    .clone()
    .applyAxisAngle(Rack.up, (Math.PI * 1) / 3)

  private static jitter(pos) {
    return roundVec(
      pos
        .clone()
        .add(
          new Vector3(
            Rack.noise * (Math.random() - 0.5),
            Rack.noise * (Math.random() - 0.5),
            0
          )
        )
    )
  }

  static cueBall(pos) {
    return new Ball(Rack.jitter(pos), 0xffffff, 0)
  }

  static swapBallPositions(b1: Ball, b2: Ball) {
    const temp = b1.pos.clone()
    b1.pos.copy(b2.pos)
    b2.pos.copy(temp)
  }

  static trianglePositions() {
    const triangle: Vector3[] = []
    const pos = new Vector3(TableGeometry.X / 2, 0, 0)
    triangle.push(vec(pos))
    pos.add(this.diagonal)
    triangle.push(vec(pos))
    pos.sub(this.across)
    triangle.push(vec(pos))
    pos.add(this.diagonal)
    triangle.push(vec(pos))
    pos.sub(this.across)
    triangle.push(vec(pos))
    pos.addScaledVector(this.across, 2)
    triangle.push(vec(pos))
    pos.add(this.diagonal)
    triangle.push(vec(pos))
    pos.sub(this.across)
    triangle.push(vec(pos))
    pos.sub(this.across)
    triangle.push(vec(pos))
    pos.sub(this.across)
    triangle.push(vec(pos))
    pos.add(this.diagonal).sub(this.across)
    triangle.push(vec(pos))
    pos.add(this.across)
    triangle.push(vec(pos))
    pos.add(this.across)
    triangle.push(vec(pos))
    pos.add(this.across)
    triangle.push(vec(pos))
    pos.add(this.across)
    triangle.push(vec(pos))
    return triangle
  }

  static triangle() {
    const tp = Rack.trianglePositions()
    const triangle: Ball[] = []
    triangle.push(Rack.cueBall(Rack.spot))
    tp.forEach((p, i) => {
      triangle.push(new Ball(Rack.jitter(p), 0, i + 1))
    })
    Rack.swapBallPositions(triangle[4], triangle[9])
    return triangle
  }

  static eightBall() {
    const triangle = this.triangle()
    Rack.swapBallPositions(triangle[4], triangle[9])
    Rack.swapBallPositions(triangle[4], triangle[8])
    Rack.swapBallPositions(triangle[3], triangle[11])
    Rack.swapBallPositions(triangle[6], triangle[14])
    return triangle
  }

  // rows[0] が先頭（手球側）の列。各行の番号配列を前方→後方へ並べてラックを組む。
  // 行間隔は球半径から幾何的に算出（gap*sin60°）、行内は ±gap で左右対称配置。
  // Ball.id は自動インクリメントのため、生成順に依らず .id=番号 となるよう都度固定する
  // （描画は b.id で色/番号を引くため id===番号 の不変条件が必須）。
  static fromRows(rows) {
    const balls = [Rack.cueBall(Rack.spot)]
    const rowStepX = (Rack.gap * Math.sqrt(3)) / 2
    const apexX = TableGeometry.X / 2
    rows.forEach((ids, r) => {
      const x = apexX + r * rowStepX
      const n = ids.length
      ids.forEach((id, i) => {
        const y = (i - (n - 1) / 2) * Rack.gap
        Ball.id = id // 次に生成する Ball の .id を番号に固定
        balls.push(new Ball(Rack.jitter(new Vector3(x, y, 0)), 0, id))
      })
    })
    return balls
  }

  // 9 ボール: ダイヤモンド（1 先頭・9 中央）。
  static nineBall() {
    return Rack.fromRows([[1], [2, 3], [4, 9, 5], [6, 7], [8]])
  }

  // 10 ボール: 三角（1 先頭・10 中央）。
  static tenBall() {
    return Rack.fromRows([[1], [2, 3], [4, 10, 5], [6, 7, 8, 9]])
  }

  // プラクティス: 手球（ヘッドスポット）+ 的玉 1 番（フットスポット）のみ。
  static practice() {
    return [
      Rack.cueBall(Rack.spot),
      new Ball(Rack.jitter(new Vector3(TableGeometry.X / 2, 0, 0)), 0, 1),
    ]
  }
}
